import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { api, formatError, imgUrl } from "../../lib/api";
import { AdminTitle, AdminField, AdminCheck, Panel } from "./AdminUi";

const EMPTY = { name: "", short_name: "", description: "", image: "", seo_title: "", seo_description: "", order: 0, active: true };

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [f, setF] = useState(null);
  const load = () => api.get("/admin/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post("/admin/upload", fd); setF((x) => ({ ...x, image: data.url })); } catch (err) { toast.error(formatError(err)); }
  };
  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, order: parseInt(f.order, 10) || 0 };
    try {
      if (f.id) await api.put(`/admin/categories/${f.id}`, body); else await api.post("/admin/categories", body);
      toast.success("Catégorie enregistrée"); setF(null); load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Supprimer « ${c.name} » ?`)) return;
    try { await api.delete(`/admin/categories/${c.id}`); toast.success("Catégorie supprimée"); load(); } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div data-testid="admin-categories">
      <AdminTitle title="Catégories" action={<button className="btn-gold !py-3" onClick={() => setF({ ...EMPTY, order: cats.length })} data-testid="admin-add-category-button"><Plus className="h-4 w-4" />Ajouter une catégorie</button>}>
        Chaque catégorie dispose automatiquement de sa propre page et de son titre SEO.
      </AdminTitle>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {cats.map((c) => (
          <Panel key={c.id} className="flex gap-4 p-4" data-testid={`admin-category-${c.slug}`}>
            <img src={imgUrl(c.image)} alt="" className="h-20 w-20 object-cover" />
            <div className="flex-1 min-w-0">
              <p className="font-display text-xl truncate">{c.name}</p>
              <p className="text-xs text-[#F8F6F1]/45">/{c.slug} · {c.product_count} produit(s) {c.active ? "" : "· masquée"}</p>
              <div className="flex gap-2 mt-2">
                <button onClick={() => setF({ ...EMPTY, ...c })} className="p-1.5 hover:text-[#C9A45C]" aria-label="Modifier" data-testid={`admin-edit-category-${c.slug}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(c)} className="p-1.5 hover:text-red-400" aria-label="Supprimer" data-testid={`admin-delete-category-${c.slug}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </Panel>
        ))}
      </div>
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent className="bg-[#101010] border-[#C9A45C]/30 max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="font-display text-2xl font-normal">{f?.id ? "Modifier la catégorie" : "Nouvelle catégorie"}</DialogTitle>
          {f && (
            <form onSubmit={save} className="grid sm:grid-cols-2 gap-4" data-testid="category-form">
              <AdminField label="Nom" required value={f.name} onChange={set("name")} testid="category-form-name" />
              <AdminField label="Nom court (menu)" value={f.short_name} onChange={set("short_name")} testid="category-form-short-name" />
              <AdminField className="sm:col-span-2" textarea rows={3} label="Description" value={f.description} onChange={set("description")} />
              <AdminField className="sm:col-span-2" label="Image (URL)" value={f.image} onChange={set("image")} />
              <label className="sm:col-span-2 text-xs text-[#C9A45C] cursor-pointer">… ou importer une image <input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="ml-2 text-[#F8F6F1]/60" /></label>
              <AdminField label="Titre SEO" value={f.seo_title} onChange={set("seo_title")} />
              <AdminField label="Ordre d'affichage" type="number" value={f.order} onChange={set("order")} />
              <AdminField className="sm:col-span-2" textarea rows={2} label="Description SEO" value={f.seo_description} onChange={set("seo_description")} />
              <AdminCheck label="Visible sur la boutique" checked={f.active} onChange={(v) => setF({ ...f, active: v })} />
              <div className="sm:col-span-2 flex justify-end gap-3">
                <button type="button" onClick={() => setF(null)} className="btn-outline-gold !py-3">Annuler</button>
                <button type="submit" className="btn-gold !py-3" data-testid="category-form-submit">Enregistrer</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
